import { FC, useRef, useState } from 'react';


import { LoadingOutlined } from '@ant-design/icons';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { Input as InputD, Switch as SwitchD, Typography } from 'antd';
import { ErrorMessage } from 'formik';
import { T } from 'react-translator-component';

import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../../../constants/googleMaps';
import { getDocuments } from '../../../../services/documents.js';
import * as S from '../styled';

const { Title } = Typography;

const Input: any = InputD;
const Switch: any = SwitchD;

type StationAddressFormProps = {
  values: any;
  handleChange: (event) => void;
  handleBlur: (event) => void;
  validateCpGroupAddress: any;
  setFieldValue: (label: string, value: any) => void;
  setFieldError: (label: string, value: any) => void;
  isSubmitting: boolean;
  errors: any;
};

const StationAddressForm: FC<StationAddressFormProps> = ({
  values,
  handleChange,
  handleBlur,
  validateCpGroupAddress,
  setFieldValue,
  setFieldError,
  isSubmitting,
  errors,
}) => {
  const [stationGeometry, setStationGeometry] = useState({
    lat: values.locationLatitude,
    lng: values.locationLongitude,
  });
  const [foundZipCode, setFoundZipCode] = useState(values.address.zipCode);
  const [loaderZipCode, setLoaderZipCode] = useState(false);
  const [loaderMap, setLoaderMap] = useState(false);

  const streetRef: any = useRef();
  const houseNumberRef: any = useRef();
  const latitudeRef: any = useRef();

  // Configuracao unica do script, compartilhada: dois carregadores com opcoes diferentes
  // injetam dois scripts do Maps e a pagina quebra. Ver src/constants/googleMaps.ts.
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_LOADER_OPTIONS);

  const user: any = JSON.parse(localStorage.getItem('auth-user') as any);

  const shouldAllowEditAddress = user?.permissions.some(
    (permission) => permission.route === '/stations/allow_edit_address' && !!permission.status,
  );

  const [enabledStreet, setEnableStreet] = useState<boolean>();
  const [disabledLatAndLng, setDisabledLatAndLng] = useState<boolean>(true);

  const COUNTRY = () => {
    switch (localStorage.getItem('theme')) {
      case 'terpel':
      case 'test-terpel':
        return 'CO';
      case 'terpel_equador':
        return 'EC';
      default:
        return 'BR';
    }
  };

  const getLocationByAddress = async (street, number) => {
    if (!shouldAllowEditAddress) {
      setLoaderMap(true);

      try {
        const city = values.address.city;
        const state = values.address.state;
        const zipCode = values.address.zipCode;

        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?addressdetails=1&format=geojson&q=${encodeURIComponent(
            `${number}, ${street}, ${city}, ${state}`,
          )}`,
        );

        const location = await response.json();

        if (location) {
          if (location.features.length === 0) {
            setDisabledLatAndLng(false);

            setTimeout(latitudeRef?.current?.focus, 0);

            return;
          }

          const filteredAddressByZipCode = location.features.filter(
            (feature) => feature.properties.address.postcode === zipCode,
          );

          setDisabledLatAndLng(true);

          setFieldValue('showLocation', true);

          if (filteredAddressByZipCode.length === 0) {
            setFieldValue('locationLongitude', location.features[0].geometry.coordinates[0]);
            setFieldValue('locationLatitude', location.features[0].geometry.coordinates[1]);

            return setStationGeometry({
              lng: location.features[0].geometry.coordinates[0],
              lat: location.features[0].geometry.coordinates[1],
            });
          }

          setFieldValue('locationLongitude', filteredAddressByZipCode[0].geometry.coordinates[0]);
          setFieldValue('locationLatitude', filteredAddressByZipCode[0].geometry.coordinates[1]);

          setStationGeometry({
            lng: filteredAddressByZipCode[0].geometry.coordinates[0],
            lat: filteredAddressByZipCode[0].geometry.coordinates[1],
          });
        }
      } catch (err) {
        //
      } finally {
        setLoaderMap(false);
      }
    }
  };

  const getLocationByZipCode = async (zipCode) => {
    setLoaderZipCode(true);
    setEnableStreet(false);

    setFieldValue('address.houseNumber', '');
    setFieldValue('address.street', '');
    setFieldValue('address.city', '');
    setFieldValue('address.state', '');
    setFieldValue('showLocation', false);
    setFieldValue('locationLatitude', '');
    setFieldValue('locationLongitude', '');
    setStationGeometry(null as any);

    try {
      if (zipCode) {
        const zipCodeResponse: any = await getDocuments(`users_data/ceps/${zipCode}`);

        if (!zipCodeResponse) {
          setFoundZipCode(false);

          return;
        }

        setFoundZipCode(true);

        const { state, street, city } = zipCodeResponse.addressForm;

        setFieldValue('address.city', city);
        setFieldValue('address.state', state);

        if (street === 'N/A') {
          setEnableStreet(true);

          setTimeout(streetRef?.current?.focus, 0);
        } else {
          setFieldValue('address.street', street);

          setTimeout(houseNumberRef?.current?.focus, 0);
        }
      }
    } catch (err) {
      setFieldError('address.zipCode', 'CEP não encontrado.');

      setFoundZipCode(false);
    } finally {
      setLoaderZipCode(false);
    }
  };

  const handleMapClick = (e) => {
    setStationGeometry({ lat: e.latLng.lat(), lng: e.latLng.lng() });

    setFieldValue('locationLatitude', e.latLng.lat());
    setFieldValue('locationLongitude', e.latLng.lng());
  };

  return (
    <>
      <S.AddressSection>
        <Title level={5}>{T('Address/Location')}</Title>
      </S.AddressSection>

      <S.FormInputGroupWrapper>
        <S.FormSmallWrapper>
          <S.FormLabel>{T('Zip code')}:</S.FormLabel>

          <Input
            name='address.zipCode'
            onChange={(e) => {
              const zipCodeFormatted: string = e.target.value.replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2');

              setFieldValue('address.zipCode', zipCodeFormatted);

              if (zipCodeFormatted.length === 9) {
                getLocationByZipCode(e.target.value);
              }
            }}
            value={values.address.zipCode}
            maxLength={9}
            disabled={(values.chargeBoxGroupPk > 0 && validateCpGroupAddress) || isSubmitting}
            size='large'
            suffix={loaderZipCode && <LoadingOutlined />}
            autoComplete='none'
          />

          {errors?.address?.zipCode && <S.MessageBox>{errors?.address?.zipCode}</S.MessageBox>}
        </S.FormSmallWrapper>
        <S.FormInputWrapper>
          <S.FormLabel>{T('Street')}:</S.FormLabel>
          <Input
            ref={streetRef}
            disabled={!shouldAllowEditAddress && !enabledStreet}
            name='address.street'
            label='address.street'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address.street}
            required
            maxLength={30}
            allowClear
            size='large'
            onKeyDown={(event: any) => {
              if (event.key === 'Enter') {
                getLocationByAddress(event.target.value, values.address.houseNumber);
              }
            }}
          />
          <ErrorMessage name='address.street' component={S.MessageBox} />
        </S.FormInputWrapper>
        <S.FormExtraSmallWrapper>
          <S.FormLabel>{T('House number')}:</S.FormLabel>
          <Input
            ref={houseNumberRef}
            disabled={!shouldAllowEditAddress && (!foundZipCode || loaderZipCode || isSubmitting)}
            name='address.houseNumber'
            label='address.houseNumber'
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);

              getLocationByAddress(values.address.street, e.target.value);
            }}
            value={values.address.houseNumber}
            required
            maxLength={5}
            size='large'
            suffix={loaderMap && <LoadingOutlined />}
            onKeyDown={(event: any) => {
              if (event.key === 'Enter') {
                getLocationByAddress(values.address.street, event.target.value);
              }
            }}
          />
          <ErrorMessage name='address.houseNumber' component={S.MessageBox} />
        </S.FormExtraSmallWrapper>
      </S.FormInputGroupWrapper>

      <S.FormInputGroupWrapper>
        <S.FormInputWrapper>
          <S.FormLabel>{T('City')}:</S.FormLabel>
          <Input
            disabled={!shouldAllowEditAddress}
            name='address.city'
            label='address.city'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address.city}
            required
            maxLength={25}
            allowClear
            size='large'
          />
          <ErrorMessage name='address.city' component={S.MessageBox} />
        </S.FormInputWrapper>

        <S.FormSmallWrapper>
          <S.FormLabel>{T('State')}:</S.FormLabel>
          <Input
            disabled={!shouldAllowEditAddress}
            name='address.state'
            label='address.state'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address.state}
            required
            maxLength={25}
            allowClear
            size='large'
          />
          <ErrorMessage name='address.state' component={S.MessageBox} />
        </S.FormSmallWrapper>

        <S.FormSmallWrapper>
          <S.FormLabel>{T('Country')}:</S.FormLabel>
          <Input
            name='address.country'
            label='address.country'
            onChange={handleChange}
            onBlur={handleBlur}
            value={(values.address.country = COUNTRY())}
            required
            maxLength={25}
            readOnly
            disabled
            size='large'
          />
          <ErrorMessage name='address.country' component={S.MessageBox} />
        </S.FormSmallWrapper>
      </S.FormInputGroupWrapper>

      <S.FormInputGroupWrapper>
        <S.FormSelectWrapper>
          <S.FormLabel>{T('Show location')}:</S.FormLabel>
          <S.SwitchWrapper>
            <Switch
              name='showLocation'
              onChange={(checked) => {
                setFieldValue('showLocation', checked);

                setFieldValue('locationLatitude', checked ? stationGeometry?.lat : '');
                setFieldValue('locationLongitude', checked ? stationGeometry?.lng : '');
              }}
              onBlur={handleBlur}
              checked={values.showLocation}
            />
          </S.SwitchWrapper>
          <ErrorMessage name='showLocation' component={S.MessageBox} />
        </S.FormSelectWrapper>
        <S.FormInputWrapper>
          <S.FormLabel>{T('Location Latitude')}:</S.FormLabel>
          <Input
            ref={latitudeRef}
            disabled={!shouldAllowEditAddress && disabledLatAndLng}
            type='number'
            name='locationLatitude'
            label='locationLatitude'
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.locationLatitude}
            maxLength={25}
            allowClear
            required
            size='large'
          />
          <ErrorMessage name='locationLatitude' component={S.MessageBox} />
        </S.FormInputWrapper>
        <S.FormInputWrapper>
          <S.FormLabel>{T('Location Longitude')}:</S.FormLabel>
          <Input
            disabled={!shouldAllowEditAddress && disabledLatAndLng}
            type='number'
            name='locationLongitude'
            label='locationLongitude'
            onChange={handleChange}
            onBlur={(e) => {
              handleBlur(e);

              setStationGeometry({ lat: values.locationLatitude, lng: Number(e.target.value) });
            }}
            value={values.locationLongitude}
            maxLength={25}
            allowClear
            required
            size='large'
          />
          <ErrorMessage name='locationLongitude' component={S.MessageBox} />
        </S.FormInputWrapper>
      </S.FormInputGroupWrapper>

      {values.showLocation && stationGeometry?.lat && isLoaded && (
        <S.StationMapWrapper>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '500px' }}
            center={stationGeometry}
            zoom={15}
            onClick={handleMapClick}
          >
            <Marker position={stationGeometry} />
          </GoogleMap>
        </S.StationMapWrapper>
      )}
    </>
  );
};

export default StationAddressForm;
